import { useCallback, useEffect, useMemo, useRef, useState, type PropsWithChildren } from "react";
import { useLocation } from "react-router-dom";
import { playAudioCue } from "../audio/audioEvents";
import { HomeNavigationContext } from "./HomeNavigationContext";
import { SiteFrame } from "../components/layout/SiteFrame";
import type { HomeSectionId } from "../content/siteContent";
import { useAudioEvents } from "../hooks/useAudioEvents";
import { useLenis } from "../hooks/useLenis";
import { useReducedMotion } from "../hooks/useReducedMotion";
import { useSectionTransition } from "../hooks/useSectionTransition";
import { scrollToTarget } from "../lib/scroll";
import { useAppStore } from "../store/useAppStore";
import { useSectionEntryStore } from "../store/useSectionEntryStore";

const homeSectionIds: HomeSectionId[] = ["studio", "projects", "team", "about", "contact"];

function isHomeSectionId(value: string): value is HomeSectionId {
  return homeSectionIds.includes(value as HomeSectionId);
}

export function AppShell({ children }: PropsWithChildren) {
  const location = useLocation();
  const headerRef = useRef<HTMLElement | null>(null);
  const currentSectionRef = useRef<HomeSectionId>("studio");
  const [navHidden, setNavHidden] = useState(false);
  const reducedMotion = useReducedMotion();
  const currentSection = useAppStore((state) => state.currentSection);
  const targetSection = useAppStore((state) => state.targetSection);
  const isTransitioning = useAppStore((state) => state.isTransitioning);
  const navOpen = useAppStore((state) => state.navOpen);
  const setNavOpen = useAppStore((state) => state.setNavOpen);
  const setReducedMotion = useAppStore((state) => state.setReducedMotion);
  const setHasEntered = useAppStore((state) => state.setHasEntered);
  const setTargetSection = useAppStore((state) => state.setTargetSection);
  const setNavigationIntent = useAppStore((state) => state.setNavigationIntent);

  useLenis({ enabled: !reducedMotion });
  useSectionTransition();
  useAudioEvents();

  useEffect(() => {
    setReducedMotion(reducedMotion);
  }, [reducedMotion, setReducedMotion]);

  useEffect(() => {
    setHasEntered(true);
  }, [setHasEntered]);

  useEffect(() => {
    currentSectionRef.current = currentSection;
  }, [currentSection]);

  useEffect(() => {
    setNavOpen(false);
  }, [location.key, setNavOpen]);

  useEffect(() => {
    const header = headerRef.current;
    if (!header) {
      return;
    }

    const root = document.documentElement;
    const syncNavOffset = () => {
      const height = header.getBoundingClientRect().height;
      root.style.setProperty("--site-nav-offset", `${Math.ceil(height)}px`);
    };

    syncNavOffset();

    const resizeObserver = new ResizeObserver(() => {
      syncNavOffset();
    });

    resizeObserver.observe(header);
    window.addEventListener("resize", syncNavOffset);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", syncNavOffset);
      root.style.removeProperty("--site-nav-offset");
    };
  }, []);

  const sectionOffset = useMemo(() => {
    return () => -((headerRef.current?.getBoundingClientRect().height ?? 0) + 12);
  }, []);

  const navigateSection = useCallback((id: HomeSectionId, source: "nav" | "hash" = "nav") => {
    const target = document.getElementById(id);
    if (!target) {
      return;
    }

    const currentIndex = homeSectionIds.indexOf(currentSectionRef.current);
    const targetIndex = homeSectionIds.indexOf(id);
    const direction = targetIndex < currentIndex ? "backward" : "forward";
    if (source === "nav") {
      playAudioCue("button");
    }
    setNavigationIntent({ source, target: id });
    useSectionEntryStore.getState().activateSection(id, performance.now(), 0, direction);
    setTargetSection(id);
    scrollToTarget(target, id === "studio" ? 0 : sectionOffset());
    window.history.replaceState({}, "", `/#${id}`);
  }, [sectionOffset, setNavigationIntent, setTargetSection]);

  useEffect(() => {
    if (location.pathname !== "/") {
      window.scrollTo({ top: 0, behavior: "auto" });
      return;
    }

    const hashTarget = location.hash.slice(1);
    const targetId: HomeSectionId = isHomeSectionId(hashTarget) ? hashTarget : "studio";

    window.requestAnimationFrame(() => {
      navigateSection(targetId, "hash");
    });
  }, [location.hash, location.pathname, navigateSection]);

  useEffect(() => {
    if (location.pathname !== "/" || isTransitioning) {
      return;
    }

    const nextHash = `#${currentSection}`;
    if (window.location.hash === nextHash) {
      return;
    }

    window.history.replaceState({}, "", `/${nextHash}`);
  }, [currentSection, isTransitioning, location.pathname]);

  useEffect(() => {
    const shouldKeepNavVisible =
      currentSection === "studio" ||
      targetSection === "studio" ||
      currentSection === "projects" ||
      targetSection === "projects" ||
      currentSection === "team" ||
      targetSection === "team" ||
      currentSection === "about" ||
      targetSection === "about" ||
      currentSection === "contact" ||
      targetSection === "contact";

    if (location.pathname !== "/" || navOpen || shouldKeepNavVisible) {
      setNavHidden(false);
      return;
    }

    let frame = 0;

    const measureOverlap = () => {
      const header = headerRef.current;
      if (!header) {
        setNavHidden(false);
        return;
      }

      const headerRect = header.getBoundingClientRect();
      const overlapBuffer = 6;
      const sections = Array.from(document.querySelectorAll<HTMLElement>(".experience-section"));
      const hasOverlap = sections.some((section) => {
        const style = window.getComputedStyle(section);
        if (style.visibility === "hidden" || style.display === "none" || Number.parseFloat(style.opacity || "1") < 0.01) {
          return false;
        }

        const rect = section.getBoundingClientRect();
        return rect.top <= headerRect.bottom + overlapBuffer && rect.bottom >= headerRect.top - overlapBuffer;
      });

      setNavHidden((hidden) => (hidden === hasOverlap ? hidden : hasOverlap));
    };

    const requestMeasure = () => {
      if (frame) {
        return;
      }

      frame = window.requestAnimationFrame(() => {
        frame = 0;
        measureOverlap();
      });
    };

    requestMeasure();
    window.addEventListener("scroll", requestMeasure, { passive: true });
    window.addEventListener("resize", requestMeasure);

    return () => {
      if (frame) {
        window.cancelAnimationFrame(frame);
      }
      window.removeEventListener("scroll", requestMeasure);
      window.removeEventListener("resize", requestMeasure);
    };
  }, [currentSection, isTransitioning, location.pathname, navOpen, targetSection]);

  return (
    <HomeNavigationContext.Provider value={{ navigateSection }}>
      <SiteFrame headerRef={headerRef} navHidden={navHidden} onNavigateSection={navigateSection}>
        {children}
      </SiteFrame>
    </HomeNavigationContext.Provider>
  );
}
